import React, {useEffect, useRef, useState} from 'react';
import {Avatar, Table, Tag, Typography} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {useTranslation} from "../../../hooks/useTranslation";
import {formatRelativeDateFromTimestamp} from "../../../utils/dateFormatter";
import {tokens} from "../../../theme/tokens";
import {useNavigate} from 'react-router-dom';


const {Text} = Typography;

interface IssueAssignee {
  id?: string;
  username?: string;
  fullName?: string;
}

interface IssueRecord {
  id: string;
  correlative?: number;
  code: string;
  description: string;
  impactDescription?: string;
  impactColor?: string;
  originDescription?: string;
  effectDescription?: string;
  createdDate?: number;
  statusDescription?: string;
  priority?: string;
  reportingUser?: IssueAssignee;
}

interface IssueGridProps {
  dataSource: IssueRecord[];
  loading: boolean;
  selectedRowKeys?: string[];
  onRowSelection?: (selectedRowKeys: string[], selectedRows: IssueRecord[]) => void;
}

const impactDotColor = (color?: string): string => {
  if (!color) return tokens.colors.neutral300;
  return color.startsWith('#') ? color : `#${color}`;
};

const priorityColor = (priority?: string): string | undefined => {
  switch ((priority || '').toLowerCase()) {
    case 'high':
    case 'alta':
    case 'critical':
    case 'crítica':
      return 'red';
    case 'medium':
    case 'media':
      return 'orange';
    case 'low':
    case 'baja':
      return 'green';
    default:
      return undefined;
  }
};

const getInitials = (name?: string): string => {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
};

const IssueGrid: React.FC<IssueGridProps> = ({
                                               dataSource,
                                               loading,
                                               onRowSelection
                                             }) => {
  const {t, language} = useTranslation();
  const navigate = useNavigate();

  // Row selection state for a single selection
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);

  // Measure the available height so the table body scrolls while the header
  // stays pinned. Recomputed whenever the container resizes.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const headerEl = el.querySelector<HTMLElement>('.ant-table-thead');
      const headerHeight = headerEl?.offsetHeight ?? 39;
      setScrollY(Math.max(el.clientHeight - headerHeight, 0));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);


  const columns = [
    {
      title: t('issues.columns.id'),
      dataIndex: 'code',
      key: 'id',
      width: 100
    },
    {
      title: t('issues.columns.title'),
      dataIndex: 'description',
      key: 'title',
      ellipsis: {showTitle: false}
    },
    {
      title: t('issues.columns.status'),
      dataIndex: 'statusDescription',
      key: 'status',
      width: 140,
      render: (value?: string) =>
        value ? <Tag color="blue" style={{margin: 0}}>{value}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: t('issues.columns.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (value?: string) =>
        value ? <Tag color={priorityColor(value)} style={{margin: 0}}>{value}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: t('issues.columns.impact'),
      dataIndex: 'impactDescription',
      key: 'impact',
      width: 170,
      render: (value: string | undefined, record: IssueRecord) => (
        <span className="gt-issue-impact">
          <span
            className="gt-issue-impact-dot"
            style={{backgroundColor: impactDotColor(record.impactColor)}}
          />
          <Text ellipsis>{value || '—'}</Text>
        </span>
      )
    },
    {
      title: t('issues.columns.assignee'),
      dataIndex: 'reportingUser',
      key: 'assignee',
      width: 200,
      render: (assignee?: IssueAssignee) => {
        if (!assignee?.fullName) return <Text type="secondary">—</Text>;
        return (
          <span className="gt-issue-assignee">
            <Avatar size="small" icon={<UserOutlined/>}>
              {getInitials(assignee.fullName)}
            </Avatar>
            <Text ellipsis>{assignee.fullName}</Text>
          </span>
        );
      }
    },
    {
      title: t('issues.columns.updated'),
      dataIndex: 'createdDate',
      key: 'updated',
      width: 120,
      render: (date?: number) => {
        if (!date) return '-';
        return formatRelativeDateFromTimestamp(date, language);
      }
    }
  ];

  return (
    <div ref={wrapperRef} style={{flex: 1, minHeight: 0}}>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{y: scrollY}}
        rowClassName={(record) =>
          selectedRowKey === record.id ? 'ant-table-row-selected-custom' : ''
        }
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
          onChange: (selectedRowKeys: React.Key[], selectedRows: IssueRecord[]) => {
            const key = selectedRowKeys[0] as string;
            setSelectedRowKey(key || null);
            if (onRowSelection) {
              onRowSelection(selectedRowKeys as string[], selectedRows);
            }
          }
        }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedRowKey(record.id);
            if (onRowSelection) onRowSelection([record.id], [record]);
          },
          onDoubleClick: () => {
            // Carry the UUID in the path and the numeric correlative (the id the
            // assessment endpoints key on) as a query param.
            const suffix = record.correlative != null ? `?correlative=${record.correlative}` : '';
            navigate(`/detail/${record.id}${suffix}`);
          }
        })}
      />
    </div>
  );
};

export default IssueGrid;
export type {IssueRecord, IssueGridProps};
